import React from "react";
import { LOGO_URL, COMPANY } from "@/lib/site-data";

// NEW HERO: Magnolia Manor — the signature estate
const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_assist-me-80/artifacts/0j67xb17_Magnolia2.JPG";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden grain"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Magnolia Manor — classical brick estate by Creative Property Development"
          className="w-full h-full object-cover"
          style={{ objectPosition: "center 8%" }}
        />
        {/* Layered overlays for readability + date-stamp masking */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/30" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.15) 35%, rgba(0,0,0,0.5) 75%, rgba(10,10,10,1) 100%)",
          }}
        />
        <div className="absolute inset-0 beam" />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-24 flex flex-col min-h-[100svh]">
        <div className="flex-1 flex flex-col justify-center max-w-4xl">
          <a
            href="#roofing"
            data-testid="hero-roofing-badge"
            className="reveal group w-fit mb-8 inline-flex items-center gap-3 border border-[var(--accent)]/50 bg-black/40 backdrop-blur-md px-5 py-2.5 transition-all duration-300 hover:border-[var(--accent)] hover:bg-black/60"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--accent)] opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--accent)]" />
            </span>
            <span className="font-body text-[11px] md:text-xs uppercase tracking-[0.2em] text-[var(--accent)]">
              New — Roof Rejuvenation
            </span>
            <span className="hidden sm:inline font-body text-[11px] md:text-xs text-[var(--text-secondary)]">
              Add 5–15 years at a fraction of replacement cost
            </span>
            <svg
              className="text-[var(--accent)] transition-transform duration-300 group-hover:translate-x-1"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </a>

          <div className="reveal flex items-center gap-4 mb-8">
            <span className="hairline w-14" />
            <span className="eyebrow" data-testid="hero-eyebrow">
              General Contractor · Roof Rejuvenation · Property Development
            </span>
          </div>

          <h1
            className="reveal reveal-delay-1 font-display text-[14vw] md:text-[7.5rem] leading-[0.95] tracking-tight text-[var(--text-primary)]"
            data-testid="hero-headline"
          >
            Building
            <br />
            <em className="font-normal text-[var(--accent)]">with vision</em>,
            <br />
            grounded in craft.
          </h1>

          <p
            className="reveal reveal-delay-2 mt-10 max-w-xl text-lg md:text-xl text-[var(--text-secondary)] font-light leading-relaxed"
            data-testid="hero-subtitle"
          >
            {COMPANY.name} serves all of New Jersey — with deep roots in
            Monmouth County — building custom homes, coastal residences, and
            historic rehabilitations, and extending the life of aging roofs
            with our rejuvenation &amp; repair service.
          </p>

          <div
            className="reveal reveal-delay-3 mt-12 flex flex-wrap items-center gap-4"
            data-testid="hero-cta-group"
          >
            <a
              href="#contact"
              className="btn-solid"
              data-testid="hero-primary-cta"
            >
              Start a Project
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </a>
            <a
              href="#work"
              className="btn-outline"
              data-testid="hero-secondary-cta"
            >
              See Our Work
            </a>
          </div>
        </div>

        {/* Stat row */}
        <div className="reveal reveal-delay-4 mt-auto pt-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-10">
          <Stat label="Projects" value="14+" />
          <Stat label="Categories" value="Six" />
          <Stat label="Delivery" value="On Time" />
          <Stat label="Range" value="Coast → Country" />
        </div>

        {/* Solar partner strip */}
        <a
          href="https://www.azimuthrenew.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="reveal reveal-delay-4 group mt-8 flex items-center gap-4 w-fit"
          data-testid="hero-azimuth-link"
        >
          <span className="bg-white rounded-lg px-4 py-3 shadow-lg">
            <img
              src="/azimuth-logo.png"
              alt="Azimuth Renewable Energy logo"
              className="h-24 w-auto object-contain"
              loading="lazy"
            />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-body text-[9px] uppercase tracking-[0.3em] text-[var(--text-secondary)]">
              Solar Partner
            </span>
            <span className="font-display text-lg text-[var(--text-primary)] group-hover:text-[var(--accent)] transition-colors duration-300">
              Azimuth Renewable Energy
            </span>
          </span>
          <svg
            className="text-[var(--accent)] opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
          >
            <path d="M7 17L17 7M7 7h10v10" />
          </svg>
        </a>
      </div>

      {/* Tiny logo watermark bottom right */}
      <img
        src={LOGO_URL}
        alt=""
        aria-hidden="true"
        className="hidden md:block absolute bottom-10 right-10 h-16 w-16 opacity-40"
      />
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-body text-[10px] uppercase tracking-[0.3em] text-[var(--text-secondary)] mb-2">
        {label}
      </div>
      <div className="font-display text-xl md:text-2xl text-[var(--text-primary)]">
        {value}
      </div>
    </div>
  );
}
